import React from 'react';
import { Phone, MessageCircle, Clock, Shield, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, seoKeywords } from '../data/api';

const HeroSection = () => {
  const handleCall = () => {
    window.location.href = `tel:${companyInfo.phoneClean}`;
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(companyInfo.whatsappMessage);
    window.open(`https://wa.me/${companyInfo.phoneClean.replace('+', '')}?text=${message}`, '_blank');
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-zinc-950">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-900/20 via-zinc-950 to-zinc-950"></div>
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-72 h-72 bg-amber-600/10 rounded-full blur-3xl"></div>
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(251,191,36,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(251,191,36,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium">7/24 Hizmet</span>
            </div>

            {/* SEO Optimized Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              <span className="text-amber-400">Denizli Taksi</span>
              <br />
              Güvenilir Ulaşım
            </h1>

            <p className="text-lg md:text-xl text-zinc-400 mb-8 max-w-xl mx-auto lg:mx-0">
              {companyInfo.name} ile Denizli'nin her noktasına hızlı, güvenli ve konforlu taksi hizmeti. 
              Havalimanı transferi, şehirlerarası yolculuk ve Pamukkale turları.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <Button
                onClick={handleCall}
                size="lg"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold text-lg px-8 py-6 shadow-xl shadow-amber-500/30 transition-all duration-300 hover:scale-105 hover:shadow-amber-500/50"
              >
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </Button>
              <Button
                onClick={handleWhatsApp}
                size="lg"
                variant="outline"
                className="border-2 border-green-500 text-green-400 hover:bg-green-500 hover:text-white font-semibold text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp ile Ulaşın
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
              <div className="flex items-center gap-2 text-zinc-400">
                <Shield className="w-5 h-5 text-amber-400" />
                <span className="text-sm">Güvenli Sürüş</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <MapPin className="w-5 h-5 text-amber-400" />
                <span className="text-sm">Denizli Geneli</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Clock className="w-5 h-5 text-amber-400" />
                <span className="text-sm">Dakik Hizmet</span>
              </div>
            </div>
          </div>

          {/* Right Content - Taxi Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main Card */}
              <div className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 backdrop-blur-sm rounded-3xl p-8 border border-amber-500/20 shadow-2xl">
                <div className="text-center">
                  <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-2xl shadow-amber-500/40">
                    <span className="text-6xl">🚕</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{companyInfo.name}</h3>
                  <p className="text-amber-400 font-medium mb-4">{companyInfo.plate}</p>
                  <div className="bg-zinc-800 rounded-xl p-4 mb-4">
                    <p className="text-zinc-400 text-sm mb-1">Hemen Arayın</p>
                    <p className="text-2xl font-bold text-amber-400">{companyInfo.phone}</p>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={handleCall} className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                      <Phone className="w-4 h-4 mr-1" /> Ara
                    </Button>
                    <Button onClick={handleWhatsApp} className="flex-1 bg-green-600 hover:bg-green-500 text-white">
                      <MessageCircle className="w-4 h-4 mr-1" /> Mesaj
                    </Button>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg animate-pulse">
                Aktif ✓
              </div>
            </div>
          </div>
        </div>

        {/* SEO Keywords Section (visually hidden but accessible) */}
        <div className="sr-only">
          <h2>Denizli Taksi Hizmetleri</h2>
          <p>{seoKeywords.join(', ')}</p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
